import { clearParkedFlows, FlowStores, relocateFlows } from "../util/flowLineRelocation";
import { session } from "../services/session";
import storage from "../util/storage";

/*
 * THE FLOW LINES, SEEDED FROM HISTORY (2026-10-19). The flow lines / ribbons used to be counted in the browser, from the moves it happened to see while the page was open: a connection
 * existed only once somebody had taken it in front of the dashboard (27 flows after two minutes of a campaign), and the rare ones - the ones into the goal - almost never. Chris,
 * 2026-10-18: "And more ribbon connections missing between these slot boxes?!?! Why?" and "There's no flow line ribbon showing up from any page slot box to /order-confirmed?!".
 *
 * Now the counts come from the site's HISTORY for the range being viewed - the backend's `getFlowCounts` (skydive_backend/src/SessionApi/Helper/flows.ts; the public demo answers it in the
 * browser, src/demo/demoBackend.ts) - and the live counting carries on on top. Seeding REPLACES the counts, and it is repeated every minute while the lines are on, so what is drawn is
 * derived from the data and can never drift (a state is derived, never accumulated across a boundary: CLAUDE.md perf note 10).
 *
 * The answer is by PAGE NAME. The pages that are not in a slot come back as `null` ("All other pages"). Counts are put in the flow stores' PARKED area under their page names, and the
 * same relocation that follows a page across a drag-and-drop brings home every flow whose pages are on the grid right now: the seed needs no slot arithmetic of its own.
 *
 * Demo and staging builds only (this folder is not in a production bundle) until the backend method is promoted.
 */
export interface FlowSeed {
  pairs: Array<{ from: string | null; to: string | null; count: number }>;
  arrivals: Array<{ page: string | null; count: number }>;
  sessions?: number;
  truncated?: boolean;
}

/** what the grid calls the "All other pages" box (clusterService getBoxLabel) */
export const OTHER_LABEL = "All other pages";
const FALLBACK_LABEL = /^Page \d+$/; // a slot with no page: not a real page

/** The pages on the grid (to ask the server about): every slot before the "All other pages" box that holds a real page, in slot order. */
export const seedPages = (labelAt: (i: number) => string, otherIndex: number): string[] => {
  const pages: string[] = [];
  for (let i = 0; i < otherIndex; i++) {
    const label = labelAt(i);
    if (label && !FALLBACK_LABEL.test(label) && label !== OTHER_LABEL && !pages.includes(label)) pages.push(label);
  }

  return pages;
};

const isCount = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n) && n > 0;
const isPage = (p: unknown): p is string | null => p === null || typeof p === "string";

/** The answer, if it has the right shape; anything else is dropped (a half-applied seed would draw a wrong picture). */
export const readSeed = (raw: unknown): FlowSeed | null => {
  const r: any = raw;
  if (!r || typeof r !== "object" || !Array.isArray(r.pairs) || !Array.isArray(r.arrivals)) return null;
  const pairs = r.pairs.filter((p: any) => p && isPage(p.from) && isPage(p.to) && isCount(p.count));
  const arrivals = r.arrivals.filter((a: any) => a && isPage(a.page) && isCount(a.count));

  return { pairs, arrivals, ...(typeof r.sessions === "number" ? { sessions: r.sessions } : {}), ...(r.truncated ? { truncated: true } : {}) };
};

const add = (map: Map<string, number>, key: string, w: number) => map.set(key, (map.get(key) || 0) + w);

/**
 * Replaces the stores' counts with the seed. Returns how many flows ended up ON the grid (the rest wait, parked under their page names, for their pages to be on it).
 * A page whose name has a "|" in it cannot be told apart from the separator the stores use for a pair, so flows through it are left out rather than mis-parsed.
 */
export const seedFlows = (stores: FlowStores, seed: FlowSeed, labelAt: (i: number) => string, slotCount: number): number => {
  stores.pathWeights.clear();
  stores.directionalPathWeights.clear();
  stores.pathWeightLabels.clear();
  clearParkedFlows(stores.parked);
  const name = (p: string | null): string => (p === null ? OTHER_LABEL : p);
  const usable = (l: string) => l.length > 0 && !l.includes("|");

  for (const p of seed.pairs) {
    const a = name(p.from);
    const b = name(p.to);
    if (a === b || !usable(a) || !usable(b) || !isCount(p.count)) continue;
    add(stores.parked.directional, `${a}|${b}`, p.count);
    add(stores.parked.pairs, [a, b].sort().join("|"), p.count);
  }
  for (const arrival of seed.arrivals) {
    const label = name(arrival.page);
    if (!usable(label) || !isCount(arrival.count)) continue;
    add(stores.parked.arrivals, label, arrival.count);
  }

  return relocateFlows(stores, labelAt, slotCount);
};

/** Asks the backend for the flow counts of `pages` over a range. Null when the call fails or the answer is not the right shape (the dashboard then keeps what it had). */
export const fetchFlowSeed = async (pages: string[], range: { startDate: string; endDate: string }): Promise<FlowSeed | null> => {
  try {
    const response: any = await session({
      jsonprc: "2.0",
      method: "getFlowCounts",
      params: { siteId: storage.get("siteId"), pages, startDate: range.startDate, endDate: range.endDate },
      id: 0,
    });

    return readSeed(response?.data?.result);
  } catch (e) {
    return null;
  }
};
